import { track } from '@vercel/analytics'

const nostoAccountId = 'shopify-23382917'

export async function sendNostoRequest({ query, variables, sessionParams = null, returnMetadata = false }) {
    try {
        // If sessionParams are provided, add them to the variables
        const requestVariables = sessionParams ? { ...variables, sessionParams } : variables

        // Specific logging for A/B test parameters
        if (requestVariables.abTests !== undefined) {
            console.log('🎯 A/B Tests SENT to Nosto:', JSON.stringify(requestVariables.abTests, null, 2))
            console.log('🎯 Segments SENT to Nosto:', JSON.stringify(requestVariables.segments, null, 2))
        }

        const result = await fetch('https://search.nosto.com/v1/graphql', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ query, variables: requestVariables }),
        })

        if (!result.ok) {
            const errorText = await result.text()
            console.error('🔍 sendNostoRequest - API error:', {
                status: result.status,
                statusText: result.statusText,
                error: errorText,
            })
            return false
        }

        const responseData = await result.json()
        const { data } = responseData

        // Specific logging for A/B test response - only log if A/B tests were requested
        // Only warn about missing abTests if they were actually requested in the query
        const abTestsRequested = requestVariables.abTests !== undefined

        if (data?.search?.abTests !== undefined && data.search.abTests.length > 0) {
            console.log('✅ A/B Tests returned from Nosto:', JSON.stringify(data.search.abTests, null, 2))
        } else if (data?.search?.abTests !== undefined && data.search.abTests.length === 0) {
            console.log('ℹ️ A/B Tests field present but empty array')
        } else if (abTestsRequested) {
            // Only warn if A/B tests were requested but not returned
            console.log('⚠️ No abTests field in response (but A/B tests were requested)')
        }
        // If abTests weren't requested, don't log anything - this is expected

        // If returnMetadata is true, return an object with data, query, and variables
        if (returnMetadata) {
            return {
                data,
                query,
                variables: requestVariables
            }
        }

        return data
    } catch (error) {
        console.error('🔍 sendNostoRequest - Error in sendNostoRequest:', error)
        track('Nosto API Error', {
            error_message: error.message || 'Unknown error',
            error_name: error.name || 'Error',
            timestamp: new Date().toISOString()
        })
        return false
    }
}

export async function getCollectionProducts(collectionId) {
    const data = await sendNostoRequest({
        query: `
            query {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryId: "${collectionId}",
                        size: 1500,
                    }
                ) {
                        products {
                        categoryPath
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                    inventoryLevel
                                ai{
                                primaryColor
                                overridingColor
                                dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                value
                                count
                                selected
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            collectionId: collectionId,
        },
    })

    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the collection products.',
            }),
        }
    }

    return {
        statusCode: 200,
        data: data,
    }
}

export async function getCollectionProductsByPath(path, sessionParams = null) {
    const data = await sendNostoRequest({
        query: `
            query ($sessionParams: InputSearchQuery) {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryPath: "${path}",
                        size: 1500
                    }
                    sessionParams: $sessionParams
                ) {
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            description
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai{
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            path: path,
            sessionParams: sessionParams || {},
        },
        sessionParams: sessionParams,
    })


    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the collection products.',
            }),
        }
    }

    return {
        statusCode: 200,
        data: data,
    }
}

export async function getProductsFromQuery(query, sessionParams = null) {
    const data = await sendNostoRequest({
        query: `query ($query: String, $products: InputSearchProducts, $sessionParams: InputSearchQuery) {
            search(
                accountId: "${nostoAccountId}"
                query: $query
                products: $products
                sessionParams: $sessionParams
            ) {
                products {
                    total
                    size
                    hits {
                        productId
                        url
                        name
                        imageUrl
                        thumbUrl
                        brand
                        availability
                        variantId
                        inventoryLevel
                        description
                        categories
                        gender
                        alternateImageUrls
                        price
                        listPrice
                        productType
                        tags1
                        customFields {
                            key
                            value
                        }
                        skus {
                            name
                            price
                            listPrice
                            url
                            id
                            inventoryLevel
                            imageUrl
                            availability
                            customFields {
                                key
                                value
                            }
                        }
                    }
                }
            }
        }`,
        variables: {
            query: query,
            products: {
                size: 1500,
            },
            sessionParams: sessionParams || {},
        },
        sessionParams: sessionParams,
    })

    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the products.',
            }),
        }
    }

    return {
        statusCode: 200,
        data: data,
    }
}

export async function getSearchResults(query, size = 500, sessionParams = null) {
    const data = await sendNostoRequest({
        query: `
            query ($query: String, $products: InputSearchProducts, $keywords: InputSearchKeywords, $sessionParams: InputSearchQuery) {
                search(
                    accountId: "${nostoAccountId}"
                    query: $query
                    products: $products
                    keywords: $keywords
                    sessionParams: $sessionParams
                ) {
                    query
                    redirect
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai {
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                    keywords {
                        hits {
                            keyword
                            _redirect
                            _highlight {
                                keyword
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            query: query,
            products: {
                size: size,
            },
            keywords: {
                size: 10
            },
            sessionParams: sessionParams || {},
        },
        sessionParams: sessionParams,
    })

    if (!data) {
        console.error('🔍 getSearchResults - No data returned from sendNostoRequest');
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the products from the query.',
            }),
        }
    }

    return {
        statusCode: 200,
        data: data,
    }
}

export async function getCollectionProductsPaginated(collectionId, offset = 0, limit = 24, sessionParams = null, segments = [], abTests = []) {
    const data = await sendNostoRequest({
        query: `
            query ($sessionParams: InputSearchQuery, $segments: [String!], $abTests: [InputSearchABTest!]) {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryId: "${collectionId}",
                        size: ${limit},
                        from: ${offset}
                    }
                    sessionParams: $sessionParams
                    segments: $segments
                    abTests: $abTests
                ) {
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai{
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                    abTests {
                        id
                        activeVariation {
                            id
                        }
                    }
                }
            }
        `,
        variables: {
            collectionId: collectionId,
            offset: offset,
            limit: limit,
            sessionParams: sessionParams || {},
            segments: segments || [],
            abTests: abTests || []
        },
        sessionParams: sessionParams,
    })

    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the collection products.',
            }),
        }
    }

    return {
        statusCode: 200,
        data: data,
        abTests: data?.search?.abTests || []
    }
}

export async function getCollectionProductsByPathPaginated(path, offset = 0, limit = 24, sessionParams = null, returnMetadata = false, segments = [], abTests = []) {
    const result = await sendNostoRequest({
        query: `
            query ($sessionParams: InputSearchQuery, $segments: [String!], $abTests: [InputSearchABTest!]) {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryPath: "${path}",
                        size: ${limit},
                        from: ${offset}
                    }
                    sessionParams: $sessionParams
                    segments: $segments
                    abTests: $abTests
                ) {
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai{
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                    abTests {
                        id
                        activeVariation {
                            id
                        }
                    }
                }
            }
        `,
        variables: {
            path: path,
            offset: offset,
            limit: limit,
            sessionParams: sessionParams || {},
            segments: segments || [],
            abTests: abTests || []
        },
        sessionParams: sessionParams,
        returnMetadata: returnMetadata,
    })

    // Handle both metadata format and legacy format
    const data = returnMetadata ? result.data : result

    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the collection products.',
            }),
        }
    }

    // Return with metadata if requested
    if (returnMetadata) {
        return {
            statusCode: 200,
            data: data,
            query: result.query,
            variables: result.variables,
            abTests: data?.search?.abTests || []
        }
    }

    return {
        statusCode: 200,
        data: data,
        abTests: data?.search?.abTests || []
    }
}

export async function getCollectionFilters(collectionId) {
    const data = await sendNostoRequest({
        query: `
            query {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryId: "${collectionId}",
                        size: 1500,
                    }
                ) {
                    products {
                        total
                        size
                        hits {
                            productId
                            skus {
                                id
                                customFields {
                                    key
                                    value
                                }
                                ai {
                                    primaryColor
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            collectionId: collectionId,
        },
    })

    if (!data) {

        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the collection filters.',
            }),
        }
    }
    return {
        statusCode: 200,
        data: data,
    }
}

export async function getCollectionProductsBySize(collectionId, size) {
    const data = await sendNostoRequest({
        query: `
            query {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryId: "${collectionId}",
                        size: 1000,
                    }
                ) {
                    products {
                        categoryPath
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai{
                                primaryColor
                                overridingColor
                                dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                value
                                count
                                selected
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            collectionId: collectionId,
        },
    })

    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the collection products.',
            }),
        }
    }

    // Filter products to only include those with the specified size
    const filteredProducts = data.search.products.hits.filter(product => {
        return product.skus.some(sku => {
            const sizeField = sku.customFields?.find(field => field.key === 'size')
            return sizeField?.value === size
        })
    })

    return {
        statusCode: 200,
        data: {
            search: {
                products: {
                    ...data.search.products,
                    hits: filteredProducts
                }
            }
        },
    }
}

export async function getCollectionProductsWithFilters(collectionId, filters = {}, sessionParams = null) {
    // Fetch all products from the collection
    const data = await sendNostoRequest({
        query: `
            query ($sessionParams: InputSearchQuery) {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryId: "${collectionId}",
                        size: 1500,
                    }
                    sessionParams: $sessionParams
                ) {
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai{
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            collectionId: collectionId,
            sessionParams: sessionParams || {},
        },
        sessionParams: sessionParams,
    })

    if (!data) {
        console.error('❌ Failed to fetch products with filters')
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the filtered collection products.',
            }),
        }
    }

    // Server-side filtering logic - filter the complete dataset
    let filteredProducts = data.search.products.hits

    // Apply size filter
    if (filters.size && filters.size.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                const sizeField = sku.customFields?.find(field => field.key === 'size')
                const sizeValue = sizeField?.value

                // Fallback: try to extract size from SKU name
                if (!sizeValue) {
                    const nameParts = (sku.name || '').split(' / ')
                    if (nameParts.length >= 2) {
                        const extractedSize = nameParts[1]?.trim()
                        // Check size matches AND inventory is available
                        return filters.size.includes(extractedSize) && parseInt(sku.inventoryLevel || 0) > 0
                    }
                }

                // Check size matches AND inventory is available
                return filters.size.includes(sizeValue) && parseInt(sku.inventoryLevel || 0) > 0
            })
        })
    }

    // Apply color filter
    if (filters.primaryColor && filters.primaryColor.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                return sku.ai?.primaryColor && filters.primaryColor.includes(sku.ai.primaryColor)
            })
        })
    }

    // Apply gender filter
    if (filters.gender && filters.gender.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                const genderField = sku.customFields?.find(field => field.key === 'gender')
                return genderField && filters.gender.includes(genderField.value)
            })
        })
    }

    // Apply product type filter
    if (filters.productType && filters.productType.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                // Check multiple sources for product type data
                let productTypeValue = null

                // 1. Check SKU customFields with different case variations
                const skuProductTypeField = sku.customFields?.find(field =>
                    field.key === 'producttype' || field.key === 'productType' || field.key === 'product_type'
                )
                if (skuProductTypeField?.value) {
                    productTypeValue = skuProductTypeField.value
                }

                // 2. Check product customFields with different case variations
                if (!productTypeValue) {
                    const productProductTypeField = product.customFields?.find(field =>
                        field.key === 'producttype' || field.key === 'productType' || field.key === 'product_type'
                    )
                    if (productProductTypeField?.value) {
                        productTypeValue = productProductTypeField.value
                    }
                }

                // 3. Check product categories
                if (!productTypeValue && product.categories) {
                    // Look for product type in categories
                    const categoryMatch = product.categories.find(cat =>
                        cat.toLowerCase().includes('dress') ||
                        cat.toLowerCase().includes('bib') ||
                        cat.toLowerCase().includes('pajama') ||
                        cat.toLowerCase().includes('romper')
                    )
                    if (categoryMatch) {
                        productTypeValue = categoryMatch
                    }
                }

                // 4. Check product tags
                if (!productTypeValue) {
                    const allTags = [
                        ...(product.tags1 || []),
                        ...(product.tags2 || []),
                        ...(product.tags3 || [])
                    ]
                    const tagMatch = allTags.find(tag =>
                        tag.toLowerCase().includes('dress') ||
                        tag.toLowerCase().includes('bib') ||
                        tag.toLowerCase().includes('pajama') ||
                        tag.toLowerCase().includes('romper')
                    )
                    if (tagMatch) {
                        productTypeValue = tagMatch
                    }
                }

                // 5. Fallback to direct properties
                if (!productTypeValue) {
                    productTypeValue = sku.product_type || sku.productType || product.product_type || product.productType
                }

                if (!productTypeValue) {
                    return false
                }

                const productTypeValueLower = productTypeValue.toLowerCase()
                const filterProductTypesLower = filters.productType.map(type => type.toLowerCase())

                const matches = filterProductTypesLower.includes(productTypeValueLower)
                return matches
            })
        })
    }

    // Apply visual filter tags
    if (filters.visualTags && filters.visualTags.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            // Combine all tag fields from the product
            const productTags = [
                ...(product.tags1 || []),
                ...(product.tags2 || []),
                ...(product.tags3 || [])
            ]

            // Convert to lowercase for case-insensitive matching
            const productTagsLower = productTags.map(tag => tag.toLowerCase())
            const visualTagsLower = filters.visualTags.map(tag => tag.toLowerCase())

            // Return true if product has any of the visual filter tags
            return visualTagsLower.some(tag => productTagsLower.includes(tag))
        })
    }

    // Apply vendor filter
    if (filters.vendor && filters.vendor.length > 0) {
        const filterVendors = filters.vendor.map(v => v.toLowerCase());
        filteredProducts = filteredProducts.filter(product => {
            const productBrand = (product.brand || '').toLowerCase();
            const match = filterVendors.includes(productBrand);
            return match;
        });
        if (filteredProducts.length === 0) {
            console.error('[nosto.js] Vendor filter resulted in 0 products. Original products:', filterVendors);
        }
    }

    return {
        statusCode: 200,
        data: {
            search: {
                products: {
                    ...data.search.products,
                    hits: filteredProducts,
                    total: filteredProducts.length
                }
            }
        },
    }
}

export async function getCollectionProductsByPathWithFilters(path, filters = {}, sessionParams = null) {
    // Fetch all products from the collection using path
    const data = await sendNostoRequest({
        query: `
            query ($sessionParams: InputSearchQuery) {
                search(
                    accountId: "${nostoAccountId}"
                    products: {
                        categoryPath: "${path}",
                        size: 1500,
                    }
                    sessionParams: $sessionParams
                ) {
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai{
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            path: path,
            sessionParams: sessionParams || {},
        },
        sessionParams: sessionParams,
    })

    if (!data) {
        console.error('❌ Failed to fetch products with filters using path')
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the filtered collection products.',
            }),
        }
    }

    // Server-side filtering logic - filter the complete dataset
    let filteredProducts = data.search.products.hits


    // Apply size filter
    if (filters.size && filters.size.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                const sizeField = sku.customFields?.find(field => field.key === 'size')
                const sizeValue = sizeField?.value

                // Fallback: try to extract size from SKU name
                if (!sizeValue) {
                    const nameParts = (sku.name || '').split(' / ')
                    if (nameParts.length >= 2) {
                        const extractedSize = nameParts[1]?.trim()
                        // Check size matches AND inventory is available
                        return filters.size.includes(extractedSize) && parseInt(sku.inventoryLevel || 0) > 0
                    }
                }

                // Check size matches AND inventory is available
                return filters.size.includes(sizeValue) && parseInt(sku.inventoryLevel || 0) > 0
            })
        })
    }

    // Apply color filter
    if (filters.primaryColor && filters.primaryColor.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                return sku.ai?.primaryColor && filters.primaryColor.includes(sku.ai.primaryColor)
            })
        })
    }

    // Apply gender filter
    if (filters.gender && filters.gender.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                const genderField = sku.customFields?.find(field => field.key === 'gender')
                return genderField && filters.gender.includes(genderField.value)
            })
        })
    }

    // Apply product type filter
    if (filters.productType && filters.productType.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            return product.skus.some(sku => {
                // Check multiple sources for product type data
                let productTypeValue = null

                // 1. Check SKU customFields with different case variations
                const skuProductTypeField = sku.customFields?.find(field =>
                    field.key === 'producttype' || field.key === 'productType' || field.key === 'product_type'
                )
                if (skuProductTypeField?.value) {
                    productTypeValue = skuProductTypeField.value
                }

                // 2. Check product customFields with different case variations
                if (!productTypeValue) {
                    const productProductTypeField = product.customFields?.find(field =>
                        field.key === 'producttype' || field.key === 'productType' || field.key === 'product_type'
                    )
                    if (productProductTypeField?.value) {
                        productTypeValue = productProductTypeField.value
                    }
                }

                // 3. Check product categories
                if (!productTypeValue && product.categories) {
                    // Look for product type in categories
                    const categoryMatch = product.categories.find(cat =>
                        cat.toLowerCase().includes('dress') ||
                        cat.toLowerCase().includes('bib') ||
                        cat.toLowerCase().includes('pajama') ||
                        cat.toLowerCase().includes('romper')
                    )
                    if (categoryMatch) {
                        productTypeValue = categoryMatch
                    }
                }

                // 4. Check product tags
                if (!productTypeValue) {
                    const allTags = [
                        ...(product.tags1 || []),
                        ...(product.tags2 || []),
                        ...(product.tags3 || [])
                    ]
                    const tagMatch = allTags.find(tag =>
                        tag.toLowerCase().includes('dress') ||
                        tag.toLowerCase().includes('bib') ||
                        tag.toLowerCase().includes('pajama') ||
                        tag.toLowerCase().includes('romper')
                    )
                    if (tagMatch) {
                        productTypeValue = tagMatch
                    }
                }

                // 5. Fallback to direct properties
                if (!productTypeValue) {
                    productTypeValue = sku.product_type || sku.productType || product.product_type || product.productType
                }

                if (!productTypeValue) {
                    return false
                }

                const productTypeValueLower = productTypeValue.toLowerCase()
                const filterProductTypesLower = filters.productType.map(type => type.toLowerCase())

                const matches = filterProductTypesLower.includes(productTypeValueLower)
                return matches
            })
        })
    }

    // Apply visual filter tags
    if (filters.visualTags && filters.visualTags.length > 0) {
        filteredProducts = filteredProducts.filter(product => {
            // Combine all tag fields from the product
            const productTags = [
                ...(product.tags1 || []),
                ...(product.tags2 || []),
                ...(product.tags3 || [])
            ]

            // Convert to lowercase for case-insensitive matching
            const productTagsLower = productTags.map(tag => tag.toLowerCase())
            const visualTagsLower = filters.visualTags.map(tag => tag.toLowerCase())

            // Return true if product has any of the visual filter tags
            return visualTagsLower.some(tag => productTagsLower.includes(tag))
        })
    }

    // Apply vendor filter
    if (filters.vendor && filters.vendor.length > 0) {
        const filterVendors = filters.vendor.map(v => v.toLowerCase());
        filteredProducts = filteredProducts.filter(product => {
            const productBrand = (product.brand || '').toLowerCase();
            const match = filterVendors.includes(productBrand);
            return match;
        });
        if (filteredProducts.length === 0) {
            console.error('[nosto.js] Vendor filter resulted in 0 products. Original products:', filterVendors);
        }
    }

    return {
        statusCode: 200,
        data: {
            search: {
                products: {
                    ...data.search.products,
                    hits: filteredProducts,
                    total: filteredProducts.length
                }
            }
        },
    }
}

export async function getSearchResultsPaginated(query, offset = 0, limit = 24, sessionParams = null, segments = [], abTests = []) {
    const data = await sendNostoRequest({
        query: `
            query ($query: String, $products: InputSearchProducts, $keywords: InputSearchKeywords, $sessionParams: InputSearchQuery, $segments: [String!], $abTests: [InputSearchABTest!]) {
                search(
                    accountId: "${nostoAccountId}"
                    query: $query
                    products: $products
                    keywords: $keywords
                    sessionParams: $sessionParams
                    segments: $segments
                    abTests: $abTests
                ) {
                    query
                    redirect
                    products {
                        categoryPath
                        total
                        size
                        hits {
                            availability
                            productId
                            name
                            url
                            imageUrl
                            price
                            listPrice
                            brand
                            categories
                            categoryIds
                            alternateImageUrls
                            tags1
                            tags2
                            tags3
                            datePublished
                            customFields {
                                key
                                value
                            }
                            skus {
                                name
                                price
                                url
                                id
                                imageUrl
                                listPrice
                                inventoryLevel
                                ai {
                                    primaryColor
                                    overridingColor
                                    dominantColors
                                }
                                customFields {
                                    key
                                    value
                                }
                            }
                        }
                        facets {
                            ... on SearchTermsFacet {
                                id
                                field
                                type
                                name
                                data {
                                    value
                                    count
                                    selected
                                }
                            }
                        }
                    }
                    keywords {
                        hits {
                            keyword
                            _redirect
                            _highlight {
                                keyword
                            }
                        }
                    }
                    abTests {
                        id
                        activeVariation {
                            id
                        }
                    }
                }
            }
        `,
        variables: {
            query: query,
            products: {
                size: limit,
                from: offset,
            },
            keywords: {
                size: 10
            },
            sessionParams: sessionParams || {},
            segments: segments || [],
            abTests: abTests || []
        },
        sessionParams: sessionParams,
    })

    if (!data) {
        return {
            statusCode: 500,
            body: JSON.stringify({
                message: 'There was a problem getting the search results.',
            }),
        }
    }

    return {
        statusCode: 200,
        data: data,
        abTests: data?.search?.abTests || []
    }
}

export async function getCollectionProductsWithFiltersPaginated(collectionId, filters = {}, offset = 0, limit = 24, sessionParams = null) {
    // First, get ALL products and apply filters server-side
    const filteredResult = await getCollectionProductsWithFilters(collectionId, filters, sessionParams)

    if (filteredResult.statusCode !== 200) {
        return filteredResult
    }

    const allFilteredProducts = filteredResult.data.search.products.hits
    const totalFiltered = allFilteredProducts.length

    // Now paginate from the filtered results
    const paginatedProducts = allFilteredProducts.slice(offset, offset + limit)
    const hasMore = (offset + limit) < totalFiltered

    return {
        statusCode: 200,
        data: {
            search: {
                products: {
                    ...filteredResult.data.search.products,
                    hits: paginatedProducts,
                    total: totalFiltered,
                    size: paginatedProducts.length,
                    from: offset,
                    has_more: hasMore
                }
            }
        },
    }
}

export async function getCollectionProductsByPathWithFiltersPaginated(path, filters = {}, offset = 0, limit = 24, sessionParams = null) {
    // First, get ALL products and apply filters server-side
    const filteredResult = await getCollectionProductsByPathWithFilters(path, filters, sessionParams)

    if (filteredResult.statusCode !== 200) {
        return filteredResult
    }

    const allFilteredProducts = filteredResult.data.search.products.hits
    const totalFiltered = allFilteredProducts.length

    // Now paginate from the filtered results
    const paginatedProducts = allFilteredProducts.slice(offset, offset + limit)
    const hasMore = (offset + limit) < totalFiltered

    return {
        statusCode: 200,
        data: {
            search: {
                products: {
                    ...filteredResult.data.search.products,
                    hits: paginatedProducts,
                    total: totalFiltered,
                    size: paginatedProducts.length,
                    from: offset,
                    has_more: hasMore
                }
            }
        },
    }
}

/**
 * Batch fetch complete variants for multiple base products
 * Used for PLP swatch enhancement - fetches ALL color variants for each base product
 *
 * @param {Array<string>} baseHandles - Array of base product handles (e.g., ['stripe-pajamas', 'polka-dot-dress'])
 * @param {Object} sessionParams - Optional Nosto session parameters for personalization
 * @returns {Promise<Object>} Map of baseHandle -> array of all variant products
 *
 * @example
 * const variants = await batchFetchCompleteVariants(['stripe-pajamas', 'polka-dot-dress'])
 * // Returns: {
 * //   'stripe-pajamas': [navyProduct, blueProduct, pinkProduct, ...],
 * //   'polka-dot-dress': [blueProduct, pinkProduct, yellowProduct, ...]
 * // }
 */
export async function batchFetchCompleteVariants(baseHandles, sessionParams = null) {
    if (!Array.isArray(baseHandles) || baseHandles.length === 0) {
        return {}
    }

    // Remove duplicates and filter out empty handles
    const uniqueHandles = [...new Set(baseHandles)].filter(handle => handle && handle.trim())

    if (uniqueHandles.length === 0) {
        return {}
    }

    // Fetch all variants in parallel for maximum performance
    const variantPromises = uniqueHandles.map(async (baseHandle) => {
        try {
            // Use getProductsFromQuery to find ALL variants of this base product
            // This matches PDP behavior - searches by handle to get all color variants
            const response = await getProductsFromQuery(baseHandle, sessionParams)

            if (response?.statusCode === 200 && response?.data?.search?.products?.hits) {
                const allVariants = response.data.search.products.hits

                // Filter to only include variants that match the base handle
                // This ensures we only get color variants, not unrelated products
                const matchingVariants = allVariants.filter(product => {
                    const productHandle = product.url?.split('/').pop()?.split('_')[0] || ''
                    return productHandle === baseHandle
                })

                return { baseHandle, variants: matchingVariants }
            } else {
                console.warn(`No variants found for ${baseHandle}:`, {
                    statusCode: response?.statusCode,
                    hasData: !!response?.data
                })
                return { baseHandle, variants: [] }
            }
        } catch (error) {
            console.error(`Error fetching variants for ${baseHandle}:`, error)
            console.error('Error details:', {
                message: error.message,
                stack: error.stack
            })
            return { baseHandle, variants: [] }
        }
    })

    // Wait for all requests to complete
    const results = await Promise.all(variantPromises)

    // Convert array of results into a map for easy lookup
    const variantMap = {}
    results.forEach(({ baseHandle, variants }) => {
        if (variants.length > 0) {
            variantMap[baseHandle] = variants
        }
    })
    return variantMap
}
